'use client';

/**
 * Проверка микрофона: не движущаяся линия, а разбор с числами.
 *
 * Семь сайтов в выдаче показывают колебание и говорят «если линия
 * шевелится — микрофон работает». Человек уходит с тем же вопросом, с
 * которым пришёл: почему меня плохо слышно. Половина спроса (164
 * формулировки из 366 в замере #176) — именно про качество, а не про
 * наличие сигнала.
 *
 * Поэтому здесь меряются громкость, фон комнаты, перегруз и запас между
 * речью и шумом, а каждое число сопровождается тем, что с ним делать.
 *
 * Микрофон запрашивается **только по нажатию**. Запрос при открытии
 * страницы пугает и роняет доверие ровно там, где мы обещаем
 * приватность: звук никуда не уходит, но человек об этом ещё не знает.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Mic, MicOff, Square, RotateCcw } from 'lucide-react';

import { buildReport, denialReason, toDbfs, type MicDenial, type MicReport } from '@/lib/mic-check';

/** Сколько записываем речь и тишину, миллисекунды. */
const SPEECH_MS = 5000;
const SILENCE_MS = 3000;

/** Что происходит прямо сейчас. */
type Stage = 'idle' | 'asking' | 'silence' | 'speech' | 'done' | 'denied';

/** Настройки дорожки, как их сообщает браузер. */
interface TrackInfo {
  label: string;
  sampleRate?: number;
  channelCount?: number;
  echoCancellation?: boolean;
  noiseSuppression?: boolean;
  autoGainControl?: boolean;
}

export function MicCheck() {
  const t = useTranslations('dh_mic_ui');

  const [stage, setStage] = useState<Stage>('idle');
  const [denial, setDenial] = useState<MicDenial>('unknown');
  const [level, setLevel] = useState(-Infinity);
  const [report, setReport] = useState<MicReport | null>(null);
  const [track, setTrack] = useState<TrackInfo | null>(null);
  const [left, setLeft] = useState(0);

  /**
   * Запись речи для прослушивания.
   *
   * Живёт только в памяти вкладки: адрес вида blob: указывает внутрь
   * браузера, и на сервер эти данные не уходят ни при каких условиях.
   */
  const [recorded, setRecorded] = useState<string | null>(null);
  const [recordedMs, setRecordedMs] = useState(0);

  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const frameRef = useRef(0);

  /** Отпускаем микрофон: индикатор записи в браузере должен погаснуть. */
  const release = useCallback(() => {
    cancelAnimationFrame(frameRef.current);

    streamRef.current?.getTracks().forEach((item) => {
      item.stop();
    });
    streamRef.current = null;

    void ctxRef.current?.close();
    ctxRef.current = null;
  }, []);

  // Адрес blob держит память вкладки, пока его не отозвать. Без этого
  // каждая новая проверка оставляла бы прошлую запись висеть.
  useEffect(
    () => () => {
      if (recorded) URL.revokeObjectURL(recorded);
    },
    [recorded],
  );

  // Уходя со страницы, микрофон тоже отпускаем: оставленный включённым
  // индикатор записи выглядит как слежка, даже если ничего не пишется.
  useEffect(() => release, [release]);

  /**
   * Собирает отсчёты в течение указанного времени.
   *
   * Одновременно обновляет показание уровня: человеку нужно видеть, что
   * происходит, иначе пять секунд молчания выглядят зависанием.
   */
  const collect = useCallback(
    (analyser: AnalyserNode, ms: number): Promise<Float32Array> =>
      new Promise((resolve) => {
        const buffer = new Float32Array(analyser.fftSize);
        const collected: number[] = [];
        const started = performance.now();

        const step = () => {
          analyser.getFloatTimeDomainData(buffer);

          let peak = 0;
          for (const value of buffer) {
            collected.push(value);
            const abs = Math.abs(value);
            if (abs > peak) peak = abs;
          }

          setLevel(toDbfs(peak));

          const passed = performance.now() - started;
          setLeft(Math.max(0, Math.ceil((ms - passed) / 1000)));

          if (passed >= ms) {
            resolve(Float32Array.from(collected));
            return;
          }
          frameRef.current = requestAnimationFrame(step);
        };

        frameRef.current = requestAnimationFrame(step);
      }),
    [],
  );

  /** Запрашивает микрофон и проводит оба замера. */
  const start = useCallback(async () => {
    setStage('asking');
    setReport(null);

    // Небезопасный контекст проверяется до запроса: по http (кроме
    // localhost) объекта mediaDevices нет вовсе, и обращение к нему
    // упало бы с TypeError, то есть «отказ без внятной причины»
    // вместо понятного «нужен https».
    // Тип говорит, что mediaDevices есть всегда, но по http (кроме
    // localhost) его не существует. Расширяем тип до настоящего,
    // иначе проверка выглядит лишней и её однажды снимут.
    const devices = navigator.mediaDevices as MediaDevices | undefined;

    if (!window.isSecureContext || !devices?.getUserMedia) {
      setDenial(window.isSecureContext ? 'unsupported' : 'insecure');
      setStage('denied');
      return;
    }

    let stream: MediaStream;
    try {
      // Обработку просим включённой: так звучит микрофон в звонке, а не
      // в идеальных условиях. Что именно включилось, покажем отдельно.
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (error) {
      setDenial(denialReason(error));
      setStage('denied');
      return;
    }

    streamRef.current = stream;

    const audioTrack = stream.getAudioTracks()[0];
    if (audioTrack) {
      const settings = audioTrack.getSettings();
      setTrack({
        label: audioTrack.label,
        sampleRate: settings.sampleRate,
        channelCount: settings.channelCount,
        echoCancellation: settings.echoCancellation,
        noiseSuppression: settings.noiseSuppression,
        autoGainControl: settings.autoGainControl,
      });
    }

    const ctx = new AudioContext();
    ctxRef.current = ctx;
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);

    // Сначала тишина: фон комнаты меряется, пока человек молчит.
    setStage('silence');
    const quiet = await collect(analyser, SILENCE_MS);

    setStage('speech');

    // Пишем тем же потоком: второй getUserMedia означал бы второй
    // запрос разрешения у человека, который его уже дал.
    const chunks: Blob[] = [];
    let recorder: MediaRecorder | null = null;
    try {
      recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      recorder.start();
    } catch {
      // Запись — не главное: без неё разбор всё равно состоится.
      recorder = null;
    }

    const startedAt = performance.now();
    const speech = await collect(analyser, SPEECH_MS);

    if (recorder && recorder.state !== 'inactive') {
      // Ссылка запоминается в локальной переменной: внутри промиса
      // TypeScript уже не может доказать, что она не сменилась.
      const active = recorder;
      const done = new Promise<void>((resolve) => {
        active.onstop = () => {
          resolve();
        };
      });
      recorder.stop();
      await done;

      if (chunks.length > 0) {
        setRecorded((previous) => {
          if (previous) URL.revokeObjectURL(previous);
          return URL.createObjectURL(new Blob(chunks, { type: chunks[0]?.type }));
        });
        setRecordedMs(performance.now() - startedAt);
      }
    }

    setReport(buildReport(speech, quiet));
    setStage('done');
    release();
  }, [collect, release]);

  const stop = useCallback(() => {
    release();
    setStage('idle');
    setLevel(-Infinity);
  }, [release]);

  return (
    <div className="max-w-[720px] mx-auto px-6 py-10" data-testid="mic-check">
      <h1 className="text-2xl font-bold mb-2">{t('title')}</h1>
      <p className="text-sm text-[var(--color-text-muted)] mb-8">{t('subtitle')}</p>

      {stage === 'idle' && (
        <div className="rounded-2xl border border-[var(--color-border)] p-6 text-center">
          <p className="text-sm text-[var(--color-text-muted)] mb-5">{t('intro')}</p>
          <button
            onClick={() => {
              void start();
            }}
            className="dh-cta inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium"
            data-testid="mic-start"
          >
            <Mic className="w-4 h-4" />
            {t('start')}
          </button>
          {/* Обещание приватности стоит рядом с кнопкой, а не в подвале:
              читают его ровно в момент решения, разрешать или нет. */}
          <p className="mt-4 text-xs text-[var(--color-text-muted)]">{t('privacy')}</p>
        </div>
      )}

      {stage === 'denied' && (
        <div
          className="rounded-2xl border border-red-500/40 bg-red-500/5 p-6"
          data-testid="mic-denied"
        >
          <p className="flex items-center gap-2 font-medium mb-2">
            <MicOff className="w-5 h-5" />
            {t(`denied_${denial}_title`)}
          </p>
          <p className="text-sm text-[var(--color-text-muted)] mb-4">{t(`denied_${denial}`)}</p>
          <button
            onClick={() => {
              void start();
            }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm"
          >
            <RotateCcw className="w-4 h-4" />
            {t('retry')}
          </button>
        </div>
      )}

      {(stage === 'asking' || stage === 'silence' || stage === 'speech') && (
        <div className="rounded-2xl border border-[var(--color-border)] p-6" data-testid="mic-run">
          <p className="font-medium mb-1">{t(`stage_${stage}`)}</p>
          <p className="text-sm text-[var(--color-text-muted)] mb-5">
            {stage === 'asking' ? t('stage_asking_hint') : t('seconds_left', { count: left })}
          </p>

          <LevelBar db={level} />

          <button
            onClick={stop}
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm"
          >
            <Square className="w-4 h-4" />
            {t('stop')}
          </button>
        </div>
      )}

      {stage === 'done' && report && (
        <Verdict
          report={report}
          track={track}
          onAgain={start}
          recorded={recorded}
          recordedMs={recordedMs}
        />
      )}
    </div>
  );
}

/**
 * Показание уровня.
 *
 * Полоса здесь вспомогательная, а не главная: она нужна, чтобы человек
 * видел, что запись идёт. Приговор даётся числами ниже.
 */
function LevelBar({ db }: { db: number }) {
  // -60 dBFS слева, 0 справа: ниже -60 человеческое ухо уже не отличает
  // тишину от шороха.
  const percent = db === -Infinity ? 0 : Math.max(0, Math.min(100, ((db + 60) / 60) * 100));

  return (
    <div>
      <div className="h-3 rounded-full bg-[var(--color-bg-elevated)] overflow-hidden">
        <div
          className="h-full bg-[var(--color-accent-500)] transition-[width] duration-75"
          style={{ width: `${String(percent)}%` }}
          data-testid="mic-level"
          data-db={db === -Infinity ? '-inf' : db.toFixed(1)}
        />
      </div>
      <p className="mt-2 font-mono text-xs text-[var(--color-text-muted)]">
        {db === -Infinity ? '—' : `${db.toFixed(1)} dBFS`}
      </p>
    </div>
  );
}

/** Разбор с числами и объяснением каждого. */
function Verdict({
  report,
  track,
  onAgain,
  recorded,
  recordedMs,
}: {
  report: MicReport;
  track: TrackInfo | null;
  onAgain: () => Promise<void>;
  recorded: string | null;
  recordedMs: number;
}) {
  const t = useTranslations('dh_mic_ui');
  const db = (value: number) => (value === -Infinity ? '—' : `${value.toFixed(1)} dBFS`);

  return (
    <div className="space-y-4" data-testid="mic-report">
      <Row
        tone={report.loudness === 'good' ? 'good' : report.loudness === 'clipping' ? 'bad' : 'warn'}
        title={t('row_loudness')}
        value={db(report.peakDbfs)}
        hint={t(`loudness_${report.loudness}`)}
      />
      <Row
        tone={report.noise === 'quiet' ? 'good' : report.noise === 'loud' ? 'bad' : 'warn'}
        title={t('row_noise')}
        value={db(report.noiseDbfs)}
        hint={t(`noise_${report.noise}`)}
      />
      <Row
        tone={report.quality === 'good' ? 'good' : report.quality === 'poor' ? 'bad' : 'warn'}
        title={t('row_snr')}
        value={Number.isFinite(report.snr) ? `${report.snr.toFixed(0)} dB` : '—'}
        hint={t(`snr_${report.quality}`)}
      />

      {recorded && (
        <div className="rounded-2xl border border-[var(--color-border)] p-5">
          <p className="font-medium text-sm mb-1">{t('row_listen')}</p>
          <p className="text-sm text-[var(--color-text-muted)] mb-3 leading-relaxed">
            {t('listen_hint')}
          </p>
          <audio
            controls
            src={recorded}
            className="w-full"
            data-testid="mic-playback"
            data-length-ms={Math.round(recordedMs)}
          />
        </div>
      )}

      {track && (
        <div className="rounded-2xl border border-[var(--color-border)] p-5">
          <p className="font-medium text-sm mb-3">{t('row_device')}</p>
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-[var(--color-text-muted)]">{t('device_name')}</dt>
            <dd className="truncate">{track.label || '—'}</dd>

            <dt className="text-[var(--color-text-muted)]">{t('device_rate')}</dt>
            <dd>{track.sampleRate ? `${String(track.sampleRate)} Hz` : '—'}</dd>

            <dt className="text-[var(--color-text-muted)]">{t('device_channels')}</dt>
            <dd>{track.channelCount ?? '—'}</dd>
          </dl>

          {/*
            Формулировка осторожная намеренно: getSettings сообщает, что
            браузер намерен делать, а не подтверждает, что обработка
            действительно сработала. Обещать проверенное там, где мы видим
            только заявленное, нельзя.
          */}
          <p className="mt-4 text-xs text-[var(--color-text-muted)] leading-relaxed">
            {t('device_processing', {
              echo: track.echoCancellation ? t('on') : t('off'),
              noise: track.noiseSuppression ? t('on') : t('off'),
              gain: track.autoGainControl ? t('on') : t('off'),
            })}
          </p>
        </div>
      )}

      <button
        onClick={() => {
          void onAgain();
        }}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm"
        data-testid="mic-again"
      >
        <RotateCcw className="w-4 h-4" />
        {t('again')}
      </button>
    </div>
  );
}

function Row({
  tone,
  title,
  value,
  hint,
}: {
  tone: 'good' | 'warn' | 'bad';
  title: string;
  value: string;
  hint: string;
}) {
  const color =
    tone === 'good'
      ? 'border-green-500/40 bg-green-500/5'
      : tone === 'bad'
        ? 'border-red-500/40 bg-red-500/5'
        : 'border-amber-500/40 bg-amber-500/5';

  return (
    <div className={`rounded-2xl border p-5 ${color}`}>
      <div className="flex items-baseline justify-between gap-4 mb-1">
        <p className="font-medium text-sm">{title}</p>
        <p className="font-mono text-sm" data-testid="mic-value">
          {value}
        </p>
      </div>
      <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">{hint}</p>
    </div>
  );
}
